import { authFetch } from '../auth/authFetch';
import { peekWorkspaceIdentity, redirectToSignIn, requireIdentityHeaders } from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );

export const PROVIDER_REVIEW_DECISIONS = [
  'CANDIDATE',
  'REPOSITORY_ONLY',
  'RETURN',
  'BLOCK',
];

export const PROVIDER_ROUTE_RECOMMENDATION = 'CANDIDATE';

function buildQueryString(query) {
  const params = new URLSearchParams();

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.PROVIDER);
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function createRequestError(message, status) {
  const error = new Error(message);
  error.status = status;
  if (status === 403) {
    error.authorizationError = true;
  }
  return error;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    if (response.status === 403 && message === `Request failed (${response.status})`) {
      message = 'Not authorized for this Provider action.';
    }
    throw createRequestError(message, response.status);
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query, headers } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function getProviderApiConfig() {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.PROVIDER);
  return {
    apiBaseUrl: API_BASE_URL,
    userId: identity.userId,
    orgId: identity.orgId,
  };
}

export function getProviderReviewQueue({
  page = 1,
  pageSize = 20,
  status,
  recommendation,
  assignedTo,
} = {}) {
  return request('/v1/provider/authority-review', {
    query: {
      page,
      page_size: pageSize,
      status: status || undefined,
      recommendation: recommendation || undefined,
      assigned_to: assignedTo || undefined,
    },
  });
}

export function getProviderDocument(assetId) {
  return request(`/v1/provider/documents/${assetId}`);
}

/**
 * Download asset package-file bytes for Digitize source preview.
 * @returns {Promise<{ blob: Blob, mimeType: string }>}
 */
export async function downloadProviderDocumentFileContent(assetId, fileId) {
  const response = await authFetch(
    `${API_BASE_URL}/v1/provider/documents/${assetId}/files/${fileId}/content`,
    {
      method: 'GET',
      headers: {
        ...getContextHeaders(),
      },
    }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    throw createRequestError(message, response.status);
  }
  const blob = await response.blob();
  const headerType = response.headers.get('content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream').split(';')[0].trim();
  return { blob, mimeType };
}

export function listProviderDocumentCopyContexts(assetId) {
  return request(`/v1/provider/documents/${assetId}/copy-contexts`);
}

export async function downloadProviderDocumentRendition(assetId, fileId, { disposition = 'inline' } = {}) {
  const response = await authFetch(
    `${API_BASE_URL}/v1/provider/documents/${assetId}/files/${fileId}/rendition?disposition=${encodeURIComponent(disposition)}`,
    {
      method: 'GET',
      headers: {
        ...getContextHeaders(),
      },
    }
  );
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // Keep default message when body is empty or not JSON.
    }
    throw createRequestError(message, response.status);
  }
  const blob = await response.blob();
  const headerType = response.headers.get('content-type');
  const mimeType = (headerType || blob.type || 'application/octet-stream').split(';')[0].trim();
  return { blob, mimeType };
}

export function getProviderEvidenceEvaluation(assetId) {
  return request(`/v1/provider/documents/${assetId}/evidence-evaluation`);
}

export function getProviderReviewHistory(assetId) {
  return request(`/v1/provider/documents/${assetId}/review-history`);
}

export function getProviderExtractedFacts(assetId) {
  return request(`/v1/provider/documents/${assetId}/extracted-facts`);
}

/**
 * Run ABDP-backed Digitize VA action. Backend allows Originator only;
 * Provider callers receive 403.
 * @param {'classify'|'extract'|'validate'} action
 */
export function runProviderVaAction(assetId, action) {
  return request(`/v1/provider/documents/${assetId}/va/${action}`, {
    method: 'POST',
    body: {},
  });
}

/** Loan tape attached to the document's package (read-only); 404 when none. */
export function getDocumentLoanTape(assetId) {
  return request(`/v1/provider/documents/${assetId}/loan-tape`);
}

export function getProviderAuthorityContext(assetId) {
  return request(`/v1/provider/documents/${assetId}/authority-context`);
}

export function submitProviderReviewDecision(assetId, payload) {
  return request(`/v1/provider/documents/${assetId}/review-decisions`, {
    method: 'POST',
    body: {
      decision: payload.decision,
      rationale: payload.rationale ?? null,
      ai_recommendation: payload.ai_recommendation ?? null,
      ai_recommendation_followed: Boolean(payload.ai_recommendation_followed),
    },
  });
}

export function runProviderAuthorityCheck(assetId, { notes } = {}) {
  return request(`/v1/provider/documents/${assetId}/authority-check`, {
    method: 'POST',
    body: {
      notes: notes || null,
    },
  });
}

export function assignProviderReviewTask(taskId, assignedTo) {
  return request(`/v1/provider/review-tasks/${taskId}/assign`, {
    method: 'PUT',
    body: {
      assigned_to: assignedTo,
    },
  });
}

export function routeProviderCandidates(taskIds) {
  return request('/v1/provider/authority-review/bulk-route', {
    method: 'POST',
    body: {
      task_ids: taskIds,
      recommendation: PROVIDER_ROUTE_RECOMMENDATION,
    },
  });
}

export function listProviderBatches({
  page = 1,
  pageSize = 20,
  search,
  status,
  programId,
  submitterOrg,
  sortBy = 'submit_date',
  sortOrder = 'desc',
} = {}) {
  return request('/v1/provider/batches', {
    query: {
      page,
      page_size: pageSize,
      search: search || undefined,
      status: status || undefined,
      program_id: programId || undefined,
      submitter_org: submitterOrg || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
    },
  });
}

export function getProviderBatch(batchId) {
  return request(`/v1/provider/batches/${batchId}`);
}

export function getProviderBatchItems(batchId) {
  return request(`/v1/provider/batches/${batchId}/items`);
}

export function getProviderBatchExceptions(batchId) {
  return request(`/v1/provider/batches/${batchId}/exceptions`);
}

export function getProviderBatchEvidenceReadiness(batchId) {
  return request(`/v1/provider/batches/${batchId}/evidence-readiness`);
}

export function getProviderBatchAudit(batchId) {
  return request(`/v1/provider/batches/${batchId}/audit`);
}

export function getProviderDashboardSummary() {
  return request('/v1/provider/dashboard/summary');
}

export function getProviderDashboardPipeline() {
  return request('/v1/provider/dashboard/pipeline');
}

export function getProviderDashboardCriticalAlerts() {
  return request('/v1/provider/dashboard/critical-alerts');
}

export function getProviderDashboardRecentPackages() {
  return request('/v1/provider/dashboard/recent-packages');
}

export function getProviderIntegrationHealth() {
  return request('/v1/provider/integrations/health');
}

export function getProviderAdmissionProfile(programId) {
  return request(`/v1/provider/programs/${programId}/admission-profile`);
}

export function createProviderAdmissionProfile(programId, payload) {
  return request(`/v1/provider/programs/${programId}/admission-profile`, {
    method: 'POST',
    body: payload,
  });
}

export function updateProviderAdmissionProfile(profileId, payload) {
  return request(`/v1/provider/admission-profiles/${profileId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function approveProviderAdmissionProfile(profileId) {
  return request(`/v1/provider/admission-profiles/${profileId}/approve`, {
    method: 'POST',
  });
}

export function activateProviderAdmissionProfile(profileId) {
  return request(`/v1/provider/admission-profiles/${profileId}/activate`, {
    method: 'POST',
  });
}
